

import { useEffect, useRef, useState } from 'react';
import React from 'react';

interface AccessMapProps {
  coordinates: { lat: number; lng: number };
  address?: string;
}

type MapMode = 'openstreetmap' | 'google' | 'satellite';

const MAP_MODES: { id: MapMode; name: string; url: string; subdomains?: string[]; attribution: string }[] = [
  {
    id: 'openstreetmap',
    name: 'Plan standard',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors'
  },
  {
    id: 'google',
    name: 'Plan Google Maps',
    url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    attribution: '© Google'
  },
  {
    id: 'satellite',
    name: 'Vue satellite',
    url: 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    attribution: '© Google'
  }
];

export const AccessMap = ({ coordinates, address }: AccessMapProps) => {
  const [isClient, setIsClient] = useState(false);
  const [capturedImages, setCapturedImages] = useState<{ [key in MapMode]?: string }>({});
  const [isCapturing, setIsCapturing] = useState(false);
  const mapRefs = useRef<{ [key in MapMode]?: HTMLDivElement | null }>({});
  const mapInstancesRef = useRef<{ [key in MapMode]?: any }>({});
  const tileLayerRefs = useRef<{ [key in MapMode]?: any }>({});

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Capture maps as images using html2canvas before print
  useEffect(() => {
    const captureMapsAsImages = async () => {
      try {
        // Dynamically import html2canvas
        const html2canvasModule = await import('html2canvas');
        const html2canvas = html2canvasModule.default;
        setIsCapturing(true);

        const newCapturedImages: { [key in MapMode]?: string } = {};

        // Wait a bit to ensure maps are ready
        await new Promise(resolve => setTimeout(resolve, 500));

        for (const mode of MAP_MODES) {
          const mapElement = mapRefs.current[mode.id];
          const map = mapInstancesRef.current[mode.id];

          if (!mapElement || !map) {
            console.warn(`Map element or instance not found for ${mode.id}`);
            continue;
          }

          // Ensure map is properly sized
          map.invalidateSize();

          // Wait for tiles to load - increase wait time
          await new Promise(resolve => setTimeout(resolve, 2000));

          try {
            const canvas = await html2canvas(mapElement, {
              backgroundColor: '#ffffff',
              useCORS: true,
              allowTaint: false,
              logging: false,
              scale: 2, // Higher quality
              width: mapElement.offsetWidth,
              height: mapElement.offsetHeight,
              windowWidth: mapElement.scrollWidth,
              windowHeight: mapElement.scrollHeight,
              foreignObjectRendering: true,
            });

            newCapturedImages[mode.id] = canvas.toDataURL('image/png', 0.95);
            console.log(`Successfully captured map ${mode.id}`);
          } catch (err) {
            console.error(`Failed to capture map ${mode.id}:`, err);
          }
        }

        if (Object.keys(newCapturedImages).length > 0) {
          setCapturedImages(newCapturedImages);
        }

        setIsCapturing(false);
      } catch (err) {
        console.error('Failed to import or use html2canvas. Make sure html2canvas is installed:', err);
        setIsCapturing(false);
      }
    };

    const handleBeforePrint = async () => {
      // Capture all maps as images before printing
      await captureMapsAsImages();
    };

    const handleAfterPrint = () => {
      // Force all maps to resize after print
      Object.values(mapInstancesRef.current).forEach((map) => {
        if (map) {
          setTimeout(() => {
            map.invalidateSize();
          }, 100);
        }
      });
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  useEffect(() => {
    if (!isClient) return;

    // Small delay to ensure DOM elements are mounted
    const timeoutId = setTimeout(() => {
      const initializeMaps = async () => {
        try {
          // Dynamically import Leaflet
          const L = await import('leaflet');
          //@ts-ignore
          await import('leaflet/dist/leaflet.css');

          // Fix default icons
          delete (L.Icon.Default as any).prototype._getIconUrl;
          L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          });

          // Create a custom tree icon
          const treeIcon = L.icon({
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          });

          // Initialize all three maps
          MAP_MODES.forEach((mode) => {
            const mapElement = mapRefs.current[mode.id];
            if (!mapElement || mapInstancesRef.current[mode.id]) return;

            // Check if element has dimensions
            if (mapElement.offsetWidth === 0 || mapElement.offsetHeight === 0) {
              console.warn(`Map element for ${mode.id} has no dimensions`);
              return;
            }

            // Create map instance
            const map = L.map(mapElement, {
              center: [coordinates.lat, coordinates.lng],
              zoom: 16,
              zoomControl: false,
              attributionControl: false,
              scrollWheelZoom: false,
              dragging: false,
              doubleClickZoom: false,
              boxZoom: false,
              keyboard: false,
              touchZoom: false
            });

            // Add tile layer based on mode
            const tileLayerOptions: any = {
              attribution: mode.attribution,
              maxZoom: 20
            };

            if (mode.subdomains) {
              tileLayerOptions.subdomains = mode.subdomains;
            }

            const tileLayer = L.tileLayer(mode.url, tileLayerOptions).addTo(map);
            tileLayerRefs.current[mode.id] = tileLayer;

            // Track tile loading
            let loadedTiles = 0;
            let totalTilesExpected = 0;

            tileLayer.on('tileload', (e: any) => {
              loadedTiles++;
              const tile = e.tile;
              if (tile) {
                tile.style.opacity = '1';
                tile.style.visibility = 'visible';
                tile.style.display = 'block';
              }
              map.invalidateSize();
            });

            tileLayer.on('tileerror', (e: any) => {
              console.warn(`Tile load error for ${mode.id}:`, e);
            });

            tileLayer.on('loading', () => {
              // Reset counter when loading starts
              loadedTiles = 0;
            });

            // Add marker for the tree location
            const marker = L.marker([coordinates.lat, coordinates.lng], {
              icon: treeIcon
            }).addTo(map);

            // Add popup with address if available
            if (address) {
              marker.bindPopup(`<div style="font-size: 12px; font-weight: bold;">${address}</div>`);
            }

            // Ensure map is ready
            map.whenReady(() => {
              map.invalidateSize();
            });

            mapInstancesRef.current[mode.id] = map;
          });
        } catch (err) {
          console.error('Error initializing maps:', err);
        }
      };

      initializeMaps();
    }, 100);

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutId);
      Object.values(mapInstancesRef.current).forEach((map) => {
        if (map) {
          map.remove();
        }
      });
      mapInstancesRef.current = {};
      tileLayerRefs.current = {};
    };
  }, [isClient, coordinates, address]);

  if (!isClient) {
    return (
      <div className="bg-gray-200 flex items-center justify-center p-8">
        <div className="text-center text-gray-600">
          <div className="text-sm">Chargement des cartes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 print:space-y-2 print:relative print:overflow-visible">
      {/* Maps Grid - 3 columns */}
      <div className="grid grid-cols-3 gap-3 print:gap-2 print:relative print:overflow-visible">
        {MAP_MODES.map((mode) => (
          <div key={mode.id} className="relative print:relative print:overflow-hidden print:min-h-[280px] print:max-h-[280px]">
            {/* Captured Image for Print - shown only when printing */}
            {capturedImages[mode.id] && (
              <img
                src={capturedImages[mode.id]}
                alt={mode.name}
                className="hidden print:block w-full h-full object-cover rounded border border-gray-400 absolute top-0 left-0 z-20"
                style={{
                  height: '280px',
                  minHeight: '280px',
                  maxHeight: '280px'
                }}
              />
            )}

            {/* Map Container - hidden when printing if image exists */}
            <div
              ref={(el) => {
                mapRefs.current[mode.id] = el;
              }}
              className={`w-full rounded border border-gray-300 overflow-hidden print:border-gray-400 print:relative print:overflow-hidden print:h-[280px] print:min-h-[280px] print:max-h-[280px] ${capturedImages[mode.id] ? 'print:hidden' : ''}`}
              style={{
                height: '280px',
                minHeight: '280px',
                maxHeight: '280px'
              }}
            />

            {/* Legend Label - Bottom Left */}
            <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded px-2 py-1 text-xs font-medium text-gray-800 shadow-sm print:absolute print:bottom-2 print:left-2 print:bg-white print:z-30">
              {mode.name}
            </div>
          </div>
        ))}
      </div>

      {/* Legend below maps (optional - matching the image style) */}
      <div className="flex justify-start gap-4 text-xs text-gray-600 pl-1 print:text-xs">
        {MAP_MODES.map((mode, index) => (
          <span key={mode.id}>
            {mode.name}
            {index < MAP_MODES.length - 1 && <span className="ml-4">•</span>}
          </span>
        ))}
      </div>
    </div>
  );
};

